// src/pages/About/About.jsx
import React from "react";
import "./About.css";
import Banner from "../Banner/Banner";

const About = () => {
  return (
    <section className="aboutv2">
      <div className="av2-wrap">
        {/* TOP: Story */}
        <header className="av2-head">
          <p className="av2-kicker">About our studio</p>
          <h1 className="av2-title">
            A small team helping you turn your website, ads and follow-ups into real sales.
          </h1>
          <p className="av2-sub">
            We work with business owners who are busy running the business, not learning
            marketing tools. Our job is to plan, design and run simple digital campaigns
            that bring you more leads, more calls, and more customers — without the hassle.
          </p>
        </header>

        {/* Split: who we are / what we do */}
        <section className="av2-hero">
          <div className="av2-hero-text">
            <h2 className="av2-hero-title">Why this studio exists</h2>
            <p>
              Most people feel lost with “digital marketing”: website, SEO, ads, landing
              pages, email, WhatsApp follow-ups… it’s a lot. Agencies talk in buzzwords and
              send big reports, but you just want to know: “Is this bringing me business?”
            </p>
            <p>
              We started this studio to keep things simple. We connect your website, ads and
              messages into one clear system — so visitors don’t just see you online, they
              actually enquire, book, or buy from you.
            </p>

            <div className="av2-pill-grid" role="list">
              <article className="pill-card" role="listitem">
                <span className="pill-label">What we’re good at</span>
                <ul>
                  <li>Building clean, fast websites that feel easy to use</li>
                  <li>Creating landing pages that turn clicks into enquiries</li>
                  <li>Setting up simple follow-ups by email / WhatsApp</li>
                </ul>
              </article>
              <article className="pill-card" role="listitem">
                <span className="pill-label">What we don’t do</span>
                <ul>
                  <li>No over-promising “overnight” results</li>
                  <li>No confusing dashboards you never look at</li>
                  <li>No locking you into long, complicated contracts</li>
                </ul>
              </article>
            </div>
          </div>

          <aside className="av2-hero-media" aria-label="How we support clients">
            <div className="av2-badge-meta">
              <span className="badge-dot" />
              <span className="badge-text">
                Think of us as your easy-to-reach “online marketing team”
              </span>
            </div>

            <div className="av2-hero-meta">
              <h3>How it feels to work with us</h3>
              <ul>
                <li>We speak in simple language, not marketing jargon</li>
                <li>We show you clear before/after examples and ideas</li>
                <li>We send short, clear updates — not 30-page PDFs</li>
                <li>We adjust things based on real-world results</li>
              </ul>
            </div>

            <div className="av2-micro-stats">
              <div>
                <span className="k">2–4 weeks</span>
                <span className="d">for most websites and main funnel pages</span>
              </div>
              <div>
                <span className="k">90%+</span>
                <span className="d">clients stay for ongoing improvements</span>
              </div>
            </div>
          </aside>
        </section>

        {/* VALUES ROW */}
        <section className="av2-block">
          <h2 className="av2-h2">How we think about online marketing</h2>
          <p className="av2-lead">
            Your marketing should be understandable, affordable and actually useful.
            We design everything so you always know what’s happening and why.
          </p>

          <div className="av2-values">
            {[
              {
                title: "Plain, honest advice",
                text: "We explain what we’re doing in simple words and tell you what we would do if it was our own business.",
                icon: "💬",
              },
              {
                title: "Start small, improve often",
                text: "We don’t push you to do everything at once. We launch the basics first, then keep improving based on results.",
                icon: "📈",
              },
              {
                title: "Built for real customers",
                text: "We design pages, ads and messages for normal people — busy parents, business owners, workers — not for other marketers.",
                icon: "👥",
              },
              {
                title: "You stay in control",
                text: "We set up tools in your name and share access clearly, so you always own your website, data and ad accounts.",
                icon: "🔐",
              },
            ].map((v, i) => (
              <article key={i} className="value-card">
                <div className="value-icon" aria-hidden="true">
                  {v.icon}
                </div>
                <h3>{v.title}</h3>
                <p>{v.text}</p>
              </article>
            ))}
          </div>
        </section>

        {/* USE CASES / SCENARIOS */}
        <section className="av2-block">
          <h2 className="av2-h2">Where we usually help the most</h2>
          <p className="av2-lead">
            Many projects start with a simple sentence like “Our website looks old” or
            “People click our ads but don’t contact us”. Here’s how we normally help.
          </p>

          <div className="av2-cases">
            <article className="case-card">
              <h3>Local services & appointments</h3>
              <p>
                You rely on calls, WhatsApp and walk-ins — for example clinics, salons,
                training centres, repair services and coaching. We help you turn website
                visitors into real bookings.
              </p>
              <ul>
                <li>Clear pages for each service you offer</li>
                <li>Simple enquiry / booking forms that actually get used</li>
                <li>Follow-up emails or messages so people don’t forget</li>
              </ul>
            </article>

            <article className="case-card">
              <h3>Online courses & digital products</h3>
              <p>
                You sell your knowledge through courses, workshops or digital downloads.
                We help you present your offer clearly and guide visitors to sign up.
              </p>
              <ul>
                <li>Sales pages that explain “what I get” in seconds</li>
                <li>Easy checkout flows and thank-you pages</li>
                <li>Email sequences that welcome and upsell gently</li>
              </ul>
            </article>

            <article className="case-card">
              <h3>Small brands & growing businesses</h3>
              <p>
                You’ve been getting business through referrals, but now you want your
                online presence to match the quality of your work.
              </p>
              <ul>
                <li>Modern website that looks trustworthy on mobile</li>
                <li>Basic SEO so people can actually find you</li>
                <li>Simple reporting so you see what’s working</li>
              </ul>
            </article>
          </div>
        </section>

        {/* STATS STRIP */}
        <section className="av2-stats" aria-label="Studio by the numbers">
          {[
            { k: "50+ projects", d: "websites, funnels and marketing setups delivered" },
            { k: "10+ niches", d: "from local services to online education" },
            { k: "4.9/5", d: "average client happiness across ongoing work" },
            { k: "< 24h", d: "typical response time on working days" },
          ].map((s, i) => (
            <div key={i} className="stat">
              <div className="k">{s.k}</div>
              <div className="d">{s.d}</div>
            </div>
          ))}
        </section>

        {/* LIGHT FAQ STRIP */}
        <section className="av2-block av2-faq">
          <h2 className="av2-h2">A few quick answers</h2>
          <div className="av2-faq-grid">
            <article className="faq-item">
              <h3>Do I need to know marketing terms?</h3>
              <p>
                No. You tell us your goals in simple words — more calls, more sign-ups,
                more orders — and we translate that into the right pages, campaigns and
                tools in the background.
              </p>
            </article>
            <article className="faq-item">
              <h3>Can we start small?</h3>
              <p>
                Yes. Many clients start with one clear piece: website clean-up, a new
                landing page, or a basic funnel. Once that works, we add more channels.
              </p>
            </article>
            <article className="faq-item">
              <h3>Will I be stuck with you forever?</h3>
              <p>
                No. We set everything up in your accounts and document the basics so you
                can continue with us, your team, or another partner later.
              </p>
            </article>
          </div>
        </section>

        {/* QUOTE */}
        <figure className="av2-quote">
          <blockquote>
            “They redesigned our site, set up simple follow-ups, and within a few weeks
            we were getting more real enquiries from the same ad budget.”
          </blockquote>
          <figcaption>— Client feedback</figcaption>
        </figure>

        {/* CTA GRID */}
        <section className="av2-cta" aria-label="Next steps">
          <div className="cta-card">
            <h3>Want your website to bring in more leads?</h3>
            <p>
              Share your current site and tell us what a “good month” looks like. We’ll
              come back with a simple action plan, in plain language, that you can review.
            </p>
            <a
              href="/services"
              className="btn btn-primary"
              aria-label="Explore digital marketing services"
            >
              Explore services
            </a>
          </div>
          <div className="cta-card">
            <h3>Prefer a quick chat first?</h3>
            <p>
              Send us a short message about your business and what’s not working online.
              We’ll reply with honest suggestions — even if the next step is very small.
            </p>
            <a
              href="/contact"
              className="btn btn-ghost"
              aria-label="Contact the team"
            >
              Talk to the team
            </a>
          </div>
        </section>
      </div>

      {/* Existing site banner */}
      <Banner />
    </section>
  );
};

export default About;
